import {
    ApolloClient,
    ApolloLink,
    defaultDataIdFromObject,
    HttpLink,
    InMemoryCache,
    split,
    type ServerError,
    type ServerParseError
} from "@apollo/client/core";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { createApolloProvider } from "@vue/apollo-option";

import bus from "@/bus";
import config from "@/config";
import { getIdToken } from "@/services/Auth";
import { getObjectValue } from "@/utils";

import { LocalDevLink } from "./local-dev";

let loading = 0;

type LocalContext = {
    useLocalDevLink?: boolean;
};

const errorLink = onError(({ operation, response, graphQLErrors, networkError }) => {
    if (getObjectValue(operation, "query.definitions.length") && operation.query.definitions[0].operation === "query") {
        if (graphQLErrors && getObjectValue(graphQLErrors, "length")) {
            if (response) {
                response.errors = undefined;
            }

            bus.$emit("apolloErrorEvent", {
                graphQLErrors: [...graphQLErrors],
                message: `graphQLErrors: ${graphQLErrors[0].message}`
            });
        }
    }

    /*
       We do not want the user to be redirected away from the page they are on if
       we have an error for some operations, preferring to handle the error at
       the component level.
    */
    const operationsToIgnore = [
        "PublishVariationsXmlFeed",
        "UnpublishVariationsXmlFeed",
        "DownloadDeliverable",
        "GenerateCreativeInsightsReport"
    ];

    if (networkError && !operationsToIgnore.includes(operation.operationName)) {
        if ((networkError as ServerParseError | ServerError).statusCode === 409) {
            bus.$emit("userNotFound");
            return;
        }
        if ((operation.getContext() as LocalContext).useLocalDevLink) {
            bus.$emit("localDevNetworkError");
        } else {
            bus.$emit("networkError");
        }
    }
});

// You should use an absolute URL here e.g. 'https://d1vups0kf4xobi.cloudfront.net/graphql'
const batchLink = new BatchHttpLink({ uri: `${import.meta.env.VITE_TARGET_API_URL}/v1/assemble/graphql` });
const httpLink = new HttpLink({ uri: `${import.meta.env.VITE_TARGET_API_URL}/v1/assemble/graphql` });
const baseLink = split(operation => operation.operationName === "UpdateResizedTemplate", httpLink, batchLink);

const retryLink = new RetryLink({
    delay: count => count * 1000 * Math.random(),
    attempts: {
        max: 5
    }
});

const httpWithAuthLink = setContext(async () => {
    return {
        // Add ID Token as auth header for API calls
        headers: { Authorization: await getIdToken() }
    };
}).concat(baseLink);

const localDevLink = new LocalDevLink({ uri: config.getHoxtonCliUri() });

const splitLink = retryLink.split(
    operation => {
        return !!(operation.getContext() as LocalContext).useLocalDevLink;
    },
    localDevLink,
    httpWithAuthLink
);

const cache = new InMemoryCache({
    dataIdFromObject: object => {
        const typename = "__typename";
        switch (object[typename]) {
            case "MediaItem": {
                /*
                This is required otherwise an image mediaItem that is used twice in
                a campaign will only have the resizeSettings from the first instance
                of that mediaItem.
            */
                let cacheKey = `${object[typename]}:${object.id as string}`;
                if (object.overwriteId) {
                    cacheKey += `:${object.overwriteId as string}`;
                }
                return cacheKey;
            }
            case "Editable": {
                // Because defaultValues depend on campaign we need to take them into account when creating a cache key
                return `${object[typename]}:${object._id as string}:${JSON.stringify(object.defaultValues)}`;
            }
            default:
                return defaultDataIdFromObject(object);
        }
    }
});

const apolloLink = ApolloLink.from([errorLink, splitLink]);

export const apolloClient = new ApolloClient({
    cache,
    link: apolloLink,
    connectToDevTools: true
});

export const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
    defaultOptions: {
        $loadingKey: "loading"
    },
    watchLoading(state, mod) {
        loading = state !== undefined ? loading + mod : loading;

        bus.$emit("apolloLoadEvent", loading);

        return loading;
    },
    errorHandler(error) {
        bus.$emit("apolloErrorEvent", error as Error);
    }
});
